// IronForgeLayout.js - Gym & Fitness Theme
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, InlineEditableImage,
  AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-red-200 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100 dark:border-red-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content") || field.toLowerCase().includes("story") || field.toLowerCase().includes("feature");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function IronForgeLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#dc2626";
  const secondary = T.secondaryColor || "#f97316";
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [animationComplete, setAnimationComplete] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  
  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Gym hero images - DIRECTLY FROM CONFIG
  const gymHeroImageConfigs = [
    {
      field: "themePreviewImage",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/df1f4d0c-9a7c-40da-9c86-01d65aba6d6f_a1.jpg",
    },
    {
      field: "gymHeroImage2",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b4e22219-f396-4480-9ece-eb9df3bc839c_a2.jpg",
    },
    {
      field: "gymHeroImage3",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/6b0ec00b-09a8-4282-9458-eb2925771cb2_a3.jpg",
    },
    {
      field: "gymHeroImage4",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b5046e5c-4ad6-4ae8-9166-1ce7032c1ee1_a4.jpg",
    },
    {
      field: "gymHeroImage5",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/2eadd742-3478-47a8-992d-593386f353d5_a5.jpg",
    },
  ];

  // Gym Products/Memberships - Added imageField and feature keys
  const gymProducts = [
    // Memberships
    {
      id: 1,
      nameKey: "product1_name",
      name: "Basic Membership",
      priceKey: "product1_price",
      price: 999,
      originalPriceKey: "product1_original_price",
      originalPrice: 1499,
      imageField: "product1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/11e4254f-23f6-4340-9c67-6656cf213fae_a6.jpg",
      categoryKey: "product1_category",
      category: "Memberships",
      subCategory: "Monthly",
      descriptionKey: "product1_description",
      description: "Access to all gym equipment during regular hours.",
      badge: "Popular",
      badgeColor: "#dc2626",
      features: ["Full gym access", "Locker room access", "Basic training plan"],
      featureKeys: ["product1_feature1", "product1_feature2", "product1_feature3"],
      duration: "1 Month",
      level: "Beginner"
    },
    {
      id: 2,
      nameKey: "product2_name",
      name: "Premium Membership",
      priceKey: "product2_price",
      price: 1999,
      originalPriceKey: "product2_original_price",
      originalPrice: 2999,
      imageField: "product2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b4e22219-f396-4480-9ece-eb9df3bc839c_a2.jpg",
      categoryKey: "product2_category",
      category: "Memberships",
      subCategory: "Monthly",
      descriptionKey: "product2_description",
      description: "Full access including classes, personal training, and premium facilities.",
      badge: "Bestseller",
      badgeColor: "#f97316",
      features: ["24/7 gym access", "All classes included", "Personal training", "Premium locker room"],
      featureKeys: ["product2_feature1", "product2_feature2", "product2_feature3", "product2_feature4"],
      duration: "1 Month",
      level: "All Levels"
    },
    {
      id: 3,
      nameKey: "product3_name",
      name: "Annual Membership",
      priceKey: "product3_price",
      price: 15999,
      originalPriceKey: "product3_original_price",
      originalPrice: 23999,
      imageField: "product3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/6b0ec00b-09a8-4282-9458-eb2925771cb2_a3.jpg",
      categoryKey: "product3_category",
      category: "Memberships",
      subCategory: "Annual",
      descriptionKey: "product3_description",
      description: "Best value - full premium access for an entire year.",
      badge: "Best Value",
      badgeColor: "#16a34a",
      features: ["All premium benefits", "Free guest passes", "Nutrition consultation", "2 months free"],
      featureKeys: ["product3_feature1", "product3_feature2", "product3_feature3", "product3_feature4"],
      duration: "12 Months",
      level: "All Levels"
    },
    {
      id: 4,
      nameKey: "product4_name",
      name: "Student Membership",
      priceKey: "product4_price",
      price: 799,
      originalPriceKey: "product4_original_price",
      originalPrice: 1299,
      imageField: "product4_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b5046e5c-4ad6-4ae8-9166-1ce7032c1ee1_a4.jpg",
      categoryKey: "product4_category",
      category: "Memberships",
      subCategory: "Student",
      descriptionKey: "product4_description",
      description: "Discounted membership for students with valid ID.",
      badge: "Student",
      badgeColor: "#8b5cf6",
      features: ["Full gym access", "Student hours", "Study area access"],
      featureKeys: ["product4_feature1", "product4_feature2", "product4_feature3"],
      duration: "1 Month",
      level: "Beginner"
    },
    
    // Supplements
    {
      id: 5,
      nameKey: "product5_name",
      name: "Whey Protein (2kg)",
      priceKey: "product5_price",
      price: 3499,
      originalPriceKey: "product5_original_price",
      originalPrice: 4499,
      imageField: "product5_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b4e22219-f396-4480-9ece-eb9df3bc839c_a2.jpg",
      categoryKey: "product5_category",
      category: "Supplements",
      subCategory: "Protein",
      descriptionKey: "product5_description",
      description: "Premium whey protein for muscle recovery and growth.",
      badge: "Premium",
      badgeColor: "#dc2626",
      features: ["24g protein per serving", "Fast absorption", "Delicious flavors"],
      featureKeys: ["product5_feature1", "product5_feature2", "product5_feature3"],
      duration: "2kg",
      level: "All Levels"
    },
    {
      id: 6,
      nameKey: "product6_name",
      name: "Pre-Workout",
      priceKey: "product6_price",
      price: 1999,
      originalPriceKey: "product6_original_price",
      originalPrice: 2799,
      imageField: "product6_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/6b0ec00b-09a8-4282-9458-eb2925771cb2_a3.jpg",
      categoryKey: "product6_category",
      category: "Supplements",
      subCategory: "Pre-Workout",
      descriptionKey: "product6_description",
      description: "Energy boost with focus and pump for intense workouts.",
      badge: "Energy",
      badgeColor: "#f97316",
      features: ["Increased focus", "Better pumps", "Sustained energy"],
      featureKeys: ["product6_feature1", "product6_feature2", "product6_feature3"],
      duration: "30 servings",
      level: "Intermediate"
    },
    
    // Equipment
    {
      id: 7,
      nameKey: "product7_name",
      name: "Resistance Bands Set",
      priceKey: "product7_price",
      price: 899,
      originalPriceKey: "product7_original_price",
      originalPrice: 1299,
      imageField: "product7_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/358fd388-00bb-4ec1-a591-e85ac4abf502_a7.jpg",
      categoryKey: "product7_category",
      category: "Equipment",
      subCategory: "Accessories",
      descriptionKey: "product7_description",
      description: "Complete resistance band set for home or gym workouts.",
      badge: "Essential",
      badgeColor: "#16a34a",
      features: ["5 resistance levels", "Portable", "Versatile exercises"],
      featureKeys: ["product7_feature1", "product7_feature2", "product7_feature3"],
      duration: "Set",
      level: "All Levels"
    },
    {
      id: 8,
      nameKey: "product8_name",
      name: "Gym Gloves",
      priceKey: "product8_price",
      price: 499,
      originalPriceKey: "product8_original_price",
      originalPrice: 799,
      imageField: "product8_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/70b220f2-2ec3-47ef-81a5-94d61aa429a9_a8.jpg",
      categoryKey: "product8_category",
      category: "Equipment",
      subCategory: "Accessories",
      descriptionKey: "product8_description",
      description: "Professional gym gloves for better grip and hand protection.",
      badge: "Protection",
      badgeColor: "#8b5cf6",
      features: ["Anti-slip grip", "Wrist support", "Durable material"],
      featureKeys: ["product8_feature1", "product8_feature2", "product8_feature3"],
      duration: "Pair",
      level: "All Levels"
    }
  ];

  // Gym Classes - Added imageField
  const gymClasses = [
    {
      id: 1,
      nameKey: "class1_name",
      name: "HIIT Training",
      imageField: "class1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d07b3ac5-dfd7-4af6-abdd-813bac27be00_a10.jpg",
      descKey: "class1_desc",
      desc: "High-intensity interval training for maximum calorie burn.",
      color: "#dc2626",
      scheduleKey: "class1_schedule",
      schedule: "Mon, Wed, Fri - 7:00 AM",
      durationKey: "class1_duration",
      duration: "45 min",
      levelKey: "class1_level",
      level: "Intermediate"
    },
    {
      id: 2,
      nameKey: "class2_name",
      name: "Yoga & Mobility",
      imageField: "class2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/0324a0f3-3b28-4807-a003-88c44cb92903_a9.jpg",
      descKey: "class2_desc",
      desc: "Improve flexibility, balance, and mental wellness.",
      color: "#8b5cf6",
      scheduleKey: "class2_schedule",
      schedule: "Tue, Thu, Sat - 8:00 AM",
      durationKey: "class2_duration",
      duration: "60 min",
      levelKey: "class2_level",
      level: "All Levels"
    },
    {
      id: 3,
      nameKey: "class3_name",
      name: "Strength Training",
      imageField: "class3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/4b622783-c307-49b1-bcb1-9cee14e1b20f_a11.jpg",
      descKey: "class3_desc",
      desc: "Build muscle and increase strength with proper techniques.",
      color: "#f97316",
      scheduleKey: "class3_schedule",
      schedule: "Mon, Wed, Fri - 6:00 PM",
      durationKey: "class3_duration",
      duration: "60 min",
      levelKey: "class3_level",
      level: "Intermediate"
    },
    {
      id: 4,
      nameKey: "class4_name",
      name: "CrossFit",
      imageField: "class4_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d14282c9-6a89-4a53-9a5b-895de6e93072_a12.jpg",
      descKey: "class4_desc",
      desc: "Functional fitness workouts for overall athletic performance.",
      color: "#dc2626",
      scheduleKey: "class4_schedule",
      schedule: "Tue, Thu - 7:00 PM",
      durationKey: "class4_duration",
      duration: "50 min",
      levelKey: "class4_level",
      level: "Advanced"
    }
  ];

  // Categories
  const productCategories = [
    { key: "all", icon: "💪", label: "All Products", color: "#dc2626" },
    { key: "memberships", icon: "🏷️", label: "Memberships", color: "#f97316" },
    { key: "supplements", icon: "🧪", label: "Supplements", color: "#8b5cf6" },
    { key: "equipment", icon: "🏋️", label: "Equipment", color: "#16a34a" },
  ];

  // Success Stories - Added editable fields
  const successStories = [
    {
      id: 1,
      nameKey: "story1_name",
      name: "Rajesh Kumar",
      imageField: "story1_image",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&q=80",
      resultKey: "story1_result",
      result: "Lost 25kg in 6 months",
      storyKey: "story1_story",
      story: "Iron Forge changed my life! I went from 95kg to 70kg with their amazing trainers.",
      beforeAfterKey: "story1_beforeAfter",
      beforeAfter: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&q=80"
    },
    {
      id: 2,
      nameKey: "story2_name",
      name: "Priya Sharma",
      imageField: "story2_image",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80",
      resultKey: "story2_result",
      result: "Transformed body in 4 months",
      storyKey: "story2_story",
      story: "The trainers at Iron Forge pushed me beyond my limits. Best decision ever!",
      beforeAfterKey: "story2_beforeAfter",
      beforeAfter: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&q=80"
    },
    {
      id: 3,
      nameKey: "story3_name",
      name: "Amit Patel",
      imageField: "story3_image",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80",
      resultKey: "story3_result",
      result: "Gained 12kg muscle",
      storyKey: "story3_story",
      story: "From skinny to strong - Iron Forge helped me achieve the body I always wanted.",
      beforeAfterKey: "story3_beforeAfter",
      beforeAfter: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=600&q=80"
    }
  ];

  // Fitness Tips
  const fitnessTips = [
    { id: 1, icon: "💧", titleKey: "tip1_title", title: "Stay Hydrated", descKey: "tip1_desc", desc: "Drink 3-4 liters of water daily" },
    { id: 2, icon: "🥗", titleKey: "tip2_title", title: "Balanced Nutrition", descKey: "tip2_desc", desc: "Protein, carbs, and healthy fats" },
    { id: 3, icon: "😴", titleKey: "tip3_title", title: "Quality Sleep", descKey: "tip3_desc", desc: "7-8 hours for recovery" },
    { id: 4, icon: "📝", titleKey: "tip4_title", title: "Track Progress", descKey: "tip4_desc", desc: "Log workouts and meals" },
  ];

  // Blog Posts - Added imageField
  const editorialPosts = [
    {
      id: 1,
      titleKey: "post1_title",
      title: "The Science of Strength",
      descKey: "post1_desc",
      desc: "Understanding muscle growth and recovery.",
      fullContentKey: "post1_content",
      fullContent: "Strength training is both an art and a science. Understanding how muscles grow and recover is essential for making consistent progress. Learn the principles that govern muscle hypertrophy and how to apply them to your training.",
      imageField: "post1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d07b3ac5-dfd7-4af6-abdd-813bac27be00_a10.jpg",
      dateKey: "post1_date",
      date: "March 15, 2024",
      authorKey: "post1_author",
      author: "Coach Mike",
      category: "Training"
    },
    {
      id: 2,
      titleKey: "post2_title",
      title: "Nutrition for Athletes",
      descKey: "post2_desc",
      desc: "Fuel your body for optimal performance.",
      fullContentKey: "post2_content",
      fullContent: "Proper nutrition is the foundation of athletic performance. Learn how to structure your meals around training, what supplements actually work, and how to achieve your body composition goals through smart eating.",
      imageField: "post2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/70b220f2-2ec3-47ef-81a5-94d61aa429a9_a8.jpg",
      dateKey: "post2_date",
      date: "March 10, 2024",
      authorKey: "post2_author",
      author: "Nutritionist Sarah",
      category: "Nutrition"
    },
    {
      id: 3,
      titleKey: "post3_title",
      title: "Mental Toughness",
      descKey: "post3_desc",
      desc: "Train your mind as hard as your body.",
      fullContentKey: "post3_content",
      fullContent: "The strongest muscle is the mind. Mental toughness separates those who achieve their goals from those who quit. Discover techniques to build resilience, stay motivated, and push through barriers.",
      imageField: "post3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b4e22219-f396-4480-9ece-eb9df3bc839c_a2.jpg",
      dateKey: "post3_date",
      date: "March 5, 2024",
      authorKey: "post3_author",
      author: "Mindset Coach",
      category: "Mindset"
    }
  ];

  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_memberships", label: "MEMBERSHIPS", page: "memberships" },
    { key: "nav_classes", label: "CLASSES", page: "classes" },
    { key: "nav_products", label: "PRODUCTS", page: "products" },
    { key: "nav_success", label: "SUCCESS", page: "success" },
    { key: "nav_journal", label: "JOURNAL", page: "journal" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Top bar messages
  const topBarMessages = [
    "💪 IRON FORGE GYM",
    "✦ JOIN THE COMMUNITY",
    "🏋️ PREMIUM FACILITIES",
    "✦ TRANSFORM YOUR LIFE"
  ];

  // Floating particles
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 3 + Math.random() * 5,
    delay: Math.random() * 5,
    duration: 5 + Math.random() * 5,
    opacity: 0.05 + Math.random() * 0.15,
  }));

  // Auto-rotate hero images - USING CONFIG DIRECTLY
  useEffect(() => {
    if (p.previewEditMode) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % gymHeroImageConfigs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [p.previewEditMode]);

  // Animate products
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;
    
    setTimeout(() => setAnimationComplete(true), 300);
    
    products.forEach((_, index) => {
      setTimeout(() => {
        setVisibleProducts(prev => [...prev, index]);
      }, index * 80 + 500);
    });
  }, [p.filteredProducts]);

  // Mouse move for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 15,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedPost(post);
  }, [p.previewEditMode, p.activeEditField]);

  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
  }, []);

  const openClassModal = useCallback((cls, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedClass(cls);
  }, [p.previewEditMode, p.activeEditField]);

  const closeClassModal = useCallback(() => {
    setSelectedClass(null);
  }, []);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedProduct(null);
      setSelectedPost(null);
      setSelectedClass(null);
    }
  }, [p.previewEditMode, p.activeEditField]);

  const handleEditHeroImage = useCallback((event, field = null) => {
    event.preventDefault();
    event.stopPropagation();
    if (!p.previewEditMode) {
      return;
    }
    const targetField = field || gymHeroImageConfigs[activeSlide]?.field || "themePreviewImage";
    const targetIndex = gymHeroImageConfigs.findIndex((image) => image.field === targetField);
    if (targetIndex >= 0) {
      setActiveSlide(targetIndex);
    }
    if (typeof p.onImageFieldClick === "function") {
      p.onImageFieldClick(targetField, event);
      return;
    }
    if (typeof p.onActivateField === "function") {
      p.onActivateField(targetField);
    }
  }, [activeSlide, gymHeroImageConfigs, p]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "IRON FORGE",
    title: p.storeTheme?.heroTitle || "Forge Your Best Self",
    subtitle: p.storeTheme?.heroSubtitle || "Where champions are made and limits are broken",
    primaryCta: p.storeTheme?.primaryCtaText || "Explore Memberships",
    secondaryCta: p.storeTheme?.secondaryCtaText || "View Classes",
  };

  // Get editable products with imageField
  const editableGymProducts = gymProducts.map((product) => ({
    ...product,
    image: p.storeTheme?.[product.imageField] || product.image,
  }));

  // Get editable classes with imageField
  const editableGymClasses = gymClasses.map((cls) => ({
    ...cls,
    image: p.storeTheme?.[cls.imageField] || cls.image,
  }));

  // Get editable posts with imageField
  const editableEditorialPosts = editorialPosts.map((post) => ({
    ...post,
    image: p.storeTheme?.[post.imageField] || post.image,
  }));

  // Get editable success stories with imageField
  const editableSuccessStories = successStories.map((story) => ({
    ...story,
    image: p.storeTheme?.[story.imageField] || story.image,
  }));

  // Get filtered items based on category
  const getFilteredItems = (category) => {
    if (category === "all") return editableGymProducts;
    const map = {
      "memberships": "Memberships",
      "supplements": "Supplements",
      "equipment": "Equipment",
    };
    return editableGymProducts.filter(item => item.category === map[category]);
  };

  const filteredItems = getFilteredItems(activeCategory);

  // Get card entrance animation
  const getCardEntrance = (index) => {
    const directions = [
      { x: -40, y: -15, rotate: -3 },
      { x: 40, y: -15, rotate: 3 },
      { x: -40, y: 15, rotate: -2 },
      { x: 40, y: 15, rotate: 2 },
    ];
    return directions[index % directions.length];
  };

  return (
    <div key={refreshKey} className="w-full min-h-screen bg-black overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'Inter', sans-serif" }}>
      
      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full animate-float-particle"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: `radial-gradient(circle, ${primary}${Math.floor(20 + Math.random() * 40).toString(16)}, transparent)`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              opacity: particle.opacity,
            }}
          />
        ))}
      </div>
      
      {/* Top Bar */}
      <div className="relative bg-[#1a1a1a] text-white py-2 overflow-hidden border-b border-red-500/20">
        <div className="whitespace-nowrap animate-marquee">
          {topBarMessages.map((msg, idx) => (
            <span key={idx} className="inline-block mx-8 text-xs tracking-wider text-white/80">
              <InlineEditableTextWithNav 
                p={p} 
                field={`top_bar_message_${idx + 1}`} 
                value={msg}
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          ))}
        </div>
      </div>
      
      {/* Navigation - Dark with White Text */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-black/95 backdrop-blur-xl shadow-lg py-2 border-b border-red-500/20" : "bg-black/80 backdrop-blur-sm py-4"}`}>
        <div className="w-full px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border={`border-2 ${isScrolled ? "border-red-500" : "border-red-500"}`} />
            <span className="font-bold text-xl tracking-tight text-white">
              <InlineEditableTextWithNav 
                p={p} 
                field="businessName" 
                value={p.storeInfo?.businessName || "Iron Forge"} 
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          </div>
          
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }} 
            className="md:hidden p-2"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium transition-all duration-300 relative group ${
                  activePage === item.page 
                    ? "text-red-500" 
                    : "text-white/80 hover:text-white"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-red-500 transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }} 
              className="relative group"
            >
              <div className="p-2 rounded-full transition-all duration-300 hover:bg-red-500/20">
                <FaHeart className="text-lg text-white/80 hover:text-red-500 transition-colors" />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={true} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-black shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-red-500/20">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm py-2 px-3 rounded-lg text-left ${
                  activePage === item.page 
                    ? "bg-red-500/20 text-red-500" 
                    : "text-white/80 hover:text-white"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                  className="text-inherit"
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section - FIXED: DIRECTLY USING CONFIG IMAGES - REMOVED HERO BUTTONS */}
          <section className="relative w-full h-screen min-h-[700px] overflow-hidden -mt-[60px]">
            <div className="absolute inset-0 w-full h-full">
              {gymHeroImageConfigs.map((img, idx) => (
                <div
                  key={img.field}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
                    idx === activeSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  <InlineEditableImage p={p} field={img.field} className="h-full w-full">
                    <img 
                      src={p.storeTheme?.[img.field] || img.src} 
                      alt={`Gym ${idx + 1}`} 
                      className="w-full h-full object-cover object-center" 
                      style={{ 
                        transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px) scale(1.05)`, 
                        transition: "transform 0.3s ease-out" 
                      }} 
                    />
                  </InlineEditableImage>
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent z-20 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-20 pointer-events-none" />
            </div>
            
            <div className="relative z-30 h-full w-full px-4 md:px-8 flex items-center">
              <div className="max-w-3xl">
                <div className="inline-block mb-6 animate-bounce-in">
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-black/40 backdrop-blur-md rounded-full border border-red-500/30">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-ping" />
                    <span className="text-red-400 text-xs tracking-[0.2em] uppercase">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </span>
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  </div>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.15}s` }}>{word}{" "}</span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className="mt-6 text-white/80 text-lg max-w-xl animate-slide-right">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button 
                    onClick={(e) => navigateTo("memberships", e)} 
                    className="group px-8 py-4 rounded-full bg-red-600 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-red-600/30 flex items-center gap-2 animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value={copy.primaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="memberships"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <button 
                    onClick={(e) => navigateTo("classes", e)} 
                    className="px-8 py-4 rounded-full border-2 border-red-500/50 text-white font-semibold tracking-wide transition-all duration-300 hover:bg-red-600/20 hover:border-red-500 backdrop-blur-sm animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value={copy.secondaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="classes"
                    />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow z-30">
              <div className="flex flex-col items-center gap-1">
                <span className="text-white/40 text-[10px] tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL TO EXPLORE"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </span>
                <div className="w-5 h-8 border-2 border-red-500/30 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-red-500/60 rounded-full mt-1 animate-scroll-dot" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Featured Badge - Positioned lower */}
          <div className="relative z-10 -mt-0 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-black/90 backdrop-blur-sm rounded-full shadow-lg border border-red-500/30">
              <span className="text-red-500 text-sm">💪</span>
              <span className="text-white text-xs font-semibold tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="IRON FORGE COMMUNITY"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <span className="text-red-500 text-sm">💪</span>
            </div>
          </div>
          
          {/* Fitness Tips */}
          <section className="w-full px-4 md:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {fitnessTips.map((tip, idx) => (
                <div 
                  key={tip.id} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="relative overflow-hidden rounded-2xl p-4 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-black border border-red-500/20 hover:border-red-500/50">
                    <div className="text-4xl mb-2 transition-all duration-300 group-hover:scale-110">
                      {tip.icon}
                    </div>
                    <h3 className="font-semibold text-white text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={tip.titleKey} 
                        value={tip.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </h3>
                    <p className="text-xs text-white/60 mt-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={tip.descKey} 
                        value={tip.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Category Grid */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {productCategories.map((cat, idx) => (
                <div 
                  key={cat.key} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={(e) => {
                    if (cat.key === "all") {
                      navigateTo("products", e);
                    } else {
                      setActiveCategory(cat.key);
                      navigateTo("products", e);
                    }
                  }}
                >
                  <div className="relative overflow-hidden rounded-2xl p-4 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-black border border-red-500/20 hover:border-red-500/50">
                    <div className="text-3xl mb-2 transition-all duration-300 group-hover:scale-110">
                      {cat.icon}
                    </div>
                    <h3 className="font-semibold text-white text-xs">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`cat_${cat.key}`} 
                        value={cat.label}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="products"
                      />
                    </h3>
                    <div className="mt-1 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto" style={{ backgroundColor: cat.color }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Featured Products - FIXED with InlineEditableImage */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="text-center mb-10">
              <span className="text-red-500 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_badge" 
                  value="Our Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_title" 
                  value="Top Picks"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {editableGymProducts.slice(0, 4).map((product, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = animationComplete;
                return (
                  <div 
                    key={product.id} 
                    className={`group cursor-pointer transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-[${entrance.rotate}deg]`}`}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-black border border-red-500/20 hover:border-red-500/50 transition-all duration-500 hover:-translate-y-3 hover:shadow-xl">
                      <div className="relative h-48 overflow-hidden">
                        <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        </InlineEditableImage>
                        {product.badge && (
                          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                            <InlineEditableTextWithNav 
                              p={p} 
                              field={`${product.nameKey}_badge`} 
                              value={product.badge}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            />
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-black/80 text-red-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-red-500/30">
                          {product.duration}
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-xs text-white/40">{product.category}</span>
                          <span className="text-xs text-white/20">•</span>
                          <span className="text-xs text-white/40">{product.level}</span>
                        </div>
                        <h3 className="font-semibold text-white text-sm line-clamp-1">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={product.nameKey} 
                            value={product.name}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </h3>
                        <div className="mt-2 flex items-center justify-between">
                          <div>
                            <span className="text-lg font-bold text-red-500">₹<InlineEditableTextWithNav 
                              p={p} 
                              field={product.priceKey} 
                              value={product.price.toString()}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            /></span>
                            {product.originalPrice > product.price && (
                              <span className="text-xs text-white/30 line-through ml-1">₹{product.originalPrice}</span>
                            )}
                          </div>
                          <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full border border-red-500/30">
                            {product.subCategory}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!(p.previewEditMode && p.activeEditField)) {
                    setActiveCategory("all");
                    setActivePage("products");
                    setMobileMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }} 
                className="px-8 py-3 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700 transition-all duration-300 hover:scale-105"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="view_all_button" 
                  value="View All Products"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="products"
                />
              </button>
            </div>
          </section>
          
          {/* Classes Preview - FIXED with InlineEditableImage */}
          <section className="w-full px-4 md:px-8 py-8 bg-black/50 border-t border-red-500/20 border-b border-red-500/20">
            <div className="text-center mb-8">
              <span className="text-red-500 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="classes_badge" 
                  value="Group Classes"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="classes"
                />
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {editableGymClasses.map((cls) => (
                <div 
                  key={cls.id} 
                  className="group cursor-pointer rounded-2xl overflow-hidden border border-red-500/20 hover:border-red-500/50 transition-all duration-500 hover:-translate-y-2 bg-black"
                  onClick={() => openClassModal(cls)}
                >
                  <div className="h-40 overflow-hidden">
                    <InlineEditableImage p={p} field={cls.imageField} className="block h-full w-full overflow-hidden">
                      <img src={cls.image} alt={cls.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                  </div>
                  <div className="p-3 text-center">
                    <h4 className="font-semibold text-white text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={cls.nameKey} 
                        value={cls.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="classes"
                      />
                    </h4>
                    <p className="text-xs text-white/60 mt-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={cls.descKey} 
                        value={cls.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="classes"
                      />
                    </p>
                    <div className="flex items-center justify-center gap-2 mt-2 text-xs text-white/40">
                      <span>{cls.duration}</span>
                      <span>•</span>
                      <span>{cls.level}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== MEMBERSHIPS PAGE ==================== */}
      {activePage === "memberships" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-red-500 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="memberships_badge" 
                value="Join the Community"
                onNavigate={handleNavigateAfterEdit}
                navPage="memberships"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="memberships_title" 
                value="Choose Your Plan"
                onNavigate={handleNavigateAfterEdit}
                navPage="memberships"
              />
            </h2>
            <p className="text-white/60 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="memberships_subtitle" 
                value="Select the membership that fits your fitness journey"
                onNavigate={handleNavigateAfterEdit}
                navPage="memberships"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {editableGymProducts.filter(d => d.category === "Memberships").map((product) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-black border border-red-500/20 hover:border-red-500/50 rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-3 hover:shadow-xl"
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-xl font-bold text-white">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="memberships"
                    />
                  </h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold text-red-500">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="memberships"
                    /></span>
                    <span className="text-white/40 text-sm ml-1">/{product.duration}</span>
                  </div>
                  <div className="mt-3 space-y-1">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-white/70">
                        <span className="text-red-500">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== CLASSES PAGE ==================== */}
      {activePage === "classes" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-red-500 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="classes_page_badge" 
                value="Group Classes"
                onNavigate={handleNavigateAfterEdit}
                navPage="classes"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="classes_page_title" 
                value="Train Together"
                onNavigate={handleNavigateAfterEdit}
                navPage="classes"
              />
            </h2>
            <p className="text-white/60 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="classes_page_subtitle" 
                value="Join our expert-led classes for all fitness levels"
                onNavigate={handleNavigateAfterEdit}
                navPage="classes"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {editableGymClasses.map((cls) => (
              <div 
                key={cls.id} 
                className="group cursor-pointer bg-black border border-red-500/20 hover:border-red-500/50 rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-3 hover:shadow-xl"
                onClick={() => openClassModal(cls)}
              >
                <div className="h-56 overflow-hidden">
                  <InlineEditableImage p={p} field={cls.imageField} className="block h-full w-full overflow-hidden">
                    <img src={cls.image} alt={cls.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="p-4">
                  <h3 className="text-xl font-bold text-white">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={cls.nameKey} 
                      value={cls.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="classes"
                    />
                  </h3>
                  <p className="text-white/60 text-sm mt-1">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={cls.descKey} 
                      value={cls.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="classes"
                    />
                  </p>
                  <div className="mt-3 space-y-1 text-sm text-white/50">
                    <div className="flex items-center gap-2">
                      <span className="text-red-500">📅</span>
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={cls.scheduleKey} 
                        value={cls.schedule}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="classes"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-red-500">⏱</span>
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={cls.durationKey} 
                        value={cls.duration}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="classes"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-red-500">📊</span>
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={cls.levelKey} 
                        value={cls.level}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="classes"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== PRODUCTS PAGE ==================== */}
      {activePage === "products" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-red-500 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="products_badge" 
                value="Store"
                onNavigate={handleNavigateAfterEdit}
                navPage="products"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="products_title" 
                value="Gear & Nutrition"
                onNavigate={handleNavigateAfterEdit}
                navPage="products"
              />
            </h2>
            <p className="text-white/60 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="products_subtitle" 
                value="Everything you need for your fitness journey"
                onNavigate={handleNavigateAfterEdit}
                navPage="products"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto mt-4 rounded-full" />
          </div>
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {productCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat.key 
                    ? "bg-red-600 text-white shadow-lg" 
                    : "bg-black border border-red-500/30 text-white/80 hover:border-red-500 hover:text-white"
                }`}
              >
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredItems.map((product) => (
              <div 
                key={product.id} 
                className="group cursor-pointer bg-black border border-red-500/20 hover:border-red-500/50 rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-3 hover:shadow-xl"
                onClick={() => setSelectedProduct(product)}
              >
                <div className="relative h-48 overflow-hidden">
                  <InlineEditableImage p={p} field={product.imageField} className="block h-full w-full overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: product.badgeColor }}>
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-black/80 text-red-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-red-500/30">
                    {product.duration}
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-white/40">{product.category}</span>
                    <span className="text-xs text-white/20">•</span>
                    <span className="text-xs text-white/40">{product.level}</span>
                  </div>
                  <h3 className="font-semibold text-white text-sm">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={product.nameKey} 
                      value={product.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="products"
                    />
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-bold text-red-500">₹<InlineEditableTextWithNav 
                      p={p} 
                      field={product.priceKey} 
                      value={product.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="products"
                    /></span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-white/30 line-through">₹{product.originalPrice}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏋️</div>
              <h3 className="text-xl font-semibold text-white/60">No products found in this category</h3>
            </div>
          )}
        </section>
      )}
      
      {/* ==================== SUCCESS PAGE ==================== */}
      {activePage === "success" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-red-500 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="success_badge" 
                value="Stories"
                onNavigate={handleNavigateAfterEdit}
                navPage="success"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="success_title" 
                value="Transformation Stories"
                onNavigate={handleNavigateAfterEdit}
                navPage="success"
              />
            </h2>
            <p className="text-white/60 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="success_subtitle" 
                value="Real people, real results from our Iron Forge community"
                onNavigate={handleNavigateAfterEdit}
                navPage="success"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {editableSuccessStories.map((story) => (
              <div key={story.id} className="bg-black border border-red-500/20 rounded-2xl overflow-hidden p-6 text-center">
                <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-2 border-red-500">
                  <InlineEditableImage p={p} field={story.imageField} className="block h-full w-full overflow-hidden">
                    <img src={story.image} alt={story.name} className="w-full h-full object-cover" />
                  </InlineEditableImage>
                </div>
                <h3 className="text-xl font-bold text-white mt-4">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={story.nameKey} 
                    value={story.name}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="success"
                  />
                </h3>
                <p className="text-red-500 font-semibold">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={story.resultKey} 
                    value={story.result}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="success"
                  />
                </p>
                <p className="text-white/60 text-sm mt-3">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={story.storyKey} 
                    value={story.story}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="success"
                  />
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== JOURNAL PAGE ==================== */}
      {activePage === "journal" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-red-500 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_badge" 
                value="Journal"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_title" 
                value="Fitness Insights"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </h2>
            <p className="text-white/60 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_subtitle" 
                value="Expert tips and insights for your fitness journey"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {editableEditorialPosts.map((post) => (
              <div 
                key={post.id} 
                className="group bg-black border border-red-500/20 hover:border-red-500/50 rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={(e) => openBlogModal(post, e)}
              >
                <div className="h-56 overflow-hidden">
                  <InlineEditableImage p={p} field={post.imageField} className="block h-full w-full overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-red-500 font-semibold">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`${post.titleKey}_category`} 
                        value={post.category}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                    <span className="text-xs text-white/40">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.dateKey} 
                        value={post.date}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.titleKey} 
                      value={post.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </h3>
                  <p className="text-white/60 text-sm line-clamp-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.descKey} 
                      value={post.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-white/40">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="by_prefix" 
                        value="By"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />{' '}
                      <span className="font-semibold text-white/80">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={post.authorKey} 
                          value={post.author}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="journal"
                        />
                      </span>
                    </span>
                    <button 
                      onClick={(e) => openBlogModal(post, e)}
                      className="text-red-500 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                      <span>→</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="w-full px-4 md:px-8 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-red-500 text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_badge" 
                  value="About Us"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="Forged in Fire"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </h2>
              <div className="w-16 h-1 bg-red-600 mt-4 mb-6" />
              <p className="text-white/70 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="Iron Forge was built on the belief that everyone has the potential to transform their life through fitness. We're not just a gym—we're a community of warriors committed to becoming the best versions of ourselves."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <p className="text-white/70 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="With state-of-the-art equipment, expert trainers, and a supportive environment, we provide everything you need to succeed. From beginners to athletes, we welcome everyone with open arms and a challenging spirit."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-3xl font-bold text-red-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_value" 
                      value="500+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-white/50">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_label" 
                      value="Members"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-red-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_value" 
                      value="20+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-white/50">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_label" 
                      value="Expert Trainers"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-red-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_value" 
                      value="100+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-white/50">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_label" 
                      value="Classes Monthly"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <InlineEditableImage p={p} field="gymAboutImage" className="block h-full w-full overflow-hidden">
                <img 
                  src={p.storeTheme?.gymAboutImage || "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/df1f4d0c-9a7c-40da-9c86-01d65aba6d6f_a1.jpg"} 
                  alt="About Iron Forge" 
                  className="w-full h-full object-cover" 
                />
              </InlineEditableImage>
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== PRODUCT TABS ==================== */}
      <section className="w-full px-4 md:px-8 mt-8">
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            ["products", "💪 Shop Gym"],
            ["services", "🛠 Services"],
            ["details", "📖 About Store"]
          ].map(([key, label]) => (
            <button 
              key={key} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }} 
              className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-red-600 text-white shadow-lg" : "text-white/60 hover:text-white border border-red-500/30 hover:border-red-500"}`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={`tab_${key}_label`} 
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>
      
      {/* Main Content */}
      <section className="flex-1 w-full px-4 md:px-8 py-8 md:py-12">
        <TabContent p={p} />
      </section>
      
   
      <StoreFooter p={p} />
      
      {/* ==================== MODALS ==================== */}
      
      {/* Product Modal - FIXED with InlineEditableImage and editable features */}
      {selectedProduct && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-black border border-red-500/30 rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <InlineEditableImage p={p} field={selectedProduct.imageField} className="block h-full w-full overflow-hidden">
                <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <button 
                onClick={() => setSelectedProduct(null)} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/80 text-white hover:bg-red-600 transition flex items-center justify-center text-xl border border-red-500/30"
              >
                ✕
              </button>
              {selectedProduct.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedProduct.badgeColor }}>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={`${selectedProduct.nameKey}_badge`} 
                    value={selectedProduct.badge}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </div>
              )}
              <div className="absolute bottom-3 right-3 bg-black/80 text-red-400 text-xs font-bold px-2 py-1 rounded-full border border-red-500/30">
                {selectedProduct.duration}
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-white/40">{selectedProduct.category}</span>
                <span className="text-xs text-white/20">•</span>
                <span className="text-xs text-white/40">{selectedProduct.subCategory}</span>
                <span className="text-xs text-white/20">•</span>
                <span className="text-xs text-white/40">{selectedProduct.level}</span>
              </div>
              <h3 className="text-2xl font-bold text-white">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.nameKey} 
                  value={selectedProduct.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </h3>
              <p className="text-white/60 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedProduct.descriptionKey} 
                  value={selectedProduct.description}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </p>
              <div className="mt-4">
                <p className="text-xs text-white/40 font-semibold">Features:</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {selectedProduct.features.map((feature, idx) => (
                    <span key={idx} className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full border border-red-500/30">
                      ✓ <InlineEditableTextWithNav 
                        p={p} 
                        field={selectedProduct.featureKeys[idx] || `product_feature_${idx}`} 
                        value={feature}
                        onNavigate={handleNavigateAfterEdit}
                        navPage={activePage}
                        isInModal={true}
                      />
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-3xl font-bold text-red-500">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedProduct.priceKey} 
                    value={selectedProduct.price.toString()}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                {selectedProduct.originalPrice > selectedProduct.price && (
                  <span className="text-sm text-white/30 line-through">
                    ₹<InlineEditableTextWithNav 
                      p={p} 
                      field={selectedProduct.originalPriceKey} 
                      value={selectedProduct.originalPrice.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage={activePage}
                      isInModal={true}
                    />
                  </span>
                )}
              </div>
              <div className="mt-4 text-sm text-red-500 text-center bg-red-500/10 p-3 rounded-full border border-red-500/30">
                💪 Transform your fitness journey today
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Class Modal - FIXED with InlineEditableImage and editable text */}
      {selectedClass && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-black border border-red-500/30 rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-56">
              <InlineEditableImage p={p} field={selectedClass.imageField} className="block h-full w-full overflow-hidden">
                <img src={selectedClass.image} alt={selectedClass.name} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <button 
                onClick={closeClassModal} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/80 text-white hover:bg-red-600 transition flex items-center justify-center text-xl border border-red-500/30"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <h2 className="text-2xl font-bold text-white">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedClass.nameKey} 
                  value={selectedClass.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="classes"
                  isInModal={true}
                />
              </h2>
              <p className="text-white/60 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedClass.descKey} 
                  value={selectedClass.desc}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="classes"
                  isInModal={true}
                />
              </p>
              <div className="mt-4 space-y-2 text-white/60 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-red-500">📅</span>
                  <span>Schedule: </span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedClass.scheduleKey} 
                    value={selectedClass.schedule}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="classes"
                    isInModal={true}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-red-500">⏱</span>
                  <span>Duration: </span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedClass.durationKey} 
                    value={selectedClass.duration}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="classes"
                    isInModal={true}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-red-500">📊</span>
                  <span>Level: </span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedClass.levelKey} 
                    value={selectedClass.level}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="classes"
                    isInModal={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Blog Modal - FIXED with InlineEditableImage */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-black border border-red-500/30 rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-black border-b border-red-500/20 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">Fitness Insights</h3>
              <button 
                onClick={closeBlogModal} 
                className="w-10 h-10 rounded-full bg-red-500/20 text-white hover:bg-red-600 transition flex items-center justify-center text-xl border border-red-500/30"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedPost.imageField} className="block w-full overflow-hidden rounded-xl">
                  <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-64 object-cover" />
                </InlineEditableImage>
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-red-500/20 text-red-400 text-xs rounded-full border border-red-500/30">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={`${selectedPost.titleKey}_category`} 
                    value={selectedPost.category}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                  isInModal={true}
                />
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-white/40 border-b border-red-500/20 pb-4 mb-4">
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.dateKey} 
                    value={selectedPost.date}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="by_prefix" 
                    value="By"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />{' '}
                  <span className="font-semibold text-white/80">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedPost.authorKey} 
                      value={selectedPost.author}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                      isInModal={true}
                    />
                  </span>
                </span>
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-white/70 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.fullContentKey} 
                    value={selectedPost.fullContent}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </p>
              </div>
              <div className="mt-8 pt-4 border-t border-red-500/20 flex justify-end">
                <button 
                  onClick={closeBlogModal} 
                  className="px-6 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee { animation: marquee 20s linear infinite; }
        
        @keyframes float-particle { 0%,100% { transform: translateY(0px) translateX(0px); opacity: 0.05; } 50% { transform: translateY(-40px) translateX(20px); opacity: 0.2; } }
        .animate-float-particle { animation: float-particle 6s ease-in-out infinite; }
        
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes slide-right { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.7s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.7s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes card-fly { 0% { opacity: 0; transform: translateY(40px) scale(0.9); } 60% { transform: translateY(-5px) scale(1.02); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-card-fly { animation: card-fly 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-8px); } }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        
        @keyframes slide-up-modal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up { animation: slide-up-modal 0.4s ease-out forwards; }
        
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}